/**
 * src/components/primitives/EmptyState.tsx — operator-honest empty result
 * primitive (FR-008).
 *
 * Renders the designed "no data" affordance for surfaces where a list
 * legitimately resolves to zero rows. The honest-state invariant
 * (CLAUDE.md §1, §5) requires that the operator NEVER sees a fabricated
 * row, a "—", or a plausible-but-fake datum in place of a real empty
 * result — every screen that subscribes to a list must render this
 * primitive (or the screen's bespoke equivalent) when the query resolves
 * to an empty array.
 *
 * Ported from the v.3 prototype primitive
 * (`src/components/primitives/index.tsx:497-510`). The icon is one of the
 * `ICONS` keys declared in the index file; the title and body are
 * operator-language strings (Spanish — see CLAUDE.md §12, FR-018
 * glossary gate). Action/secondary slots are caller-provided so screens
 * can wire affordances like "Subir contenido" or "Crear canal".
 *
 * Distinct from `Skeleton`: this primitive renders when data has been
 * fetched and resolved to empty. `Skeleton` renders while a fetch is
 * in flight. Mixing the two is the bug the honest-state rule catches:
 * a permanent skeleton lies about loading state; a permanent
 * EmptyState (when really still loading) lies about the data.
 */
import React, { type CSSProperties, type ReactNode } from 'react';
import { Icon } from './index';

export interface EmptyStateProps {
  /** Icon name from `ICONS` (defaults to `folder`). */
  icon?: string;
  /** Primary headline — operator-language string. */
  title: string;
  /** Optional supporting copy. */
  body?: ReactNode;
  /** Primary action affordance. */
  action?: ReactNode;
  /** Optional secondary action. */
  secondary?: ReactNode;
  /** Optional helper tiles row (e.g. quick-link cards). */
  helpers?: ReactNode;
}

const WRAPPER_STYLE: CSSProperties = {
  textAlign: 'center',
  padding: '48px 24px',
  maxWidth: 540,
  margin: '0 auto',
};

const ICON_WELL_STYLE: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'var(--snow)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 18,
};

const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 24,
  fontWeight: 400,
  letterSpacing: '-0.3px',
};

const BODY_STYLE: CSSProperties = {
  margin: '10px 0 0',
  fontSize: 15,
  color: 'var(--body-muted)',
  lineHeight: 1.5,
};

const HELPERS_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 12,
  margin: '24px 0',
};

const ACTION_ROW_STYLE: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
  marginTop: 22,
};

export function EmptyState({
  icon = 'folder',
  title,
  body,
  action,
  secondary,
  helpers,
}: EmptyStateProps): React.ReactElement {
  return (
    <div style={WRAPPER_STYLE} data-circa-empty-state="true">
      <div style={ICON_WELL_STYLE}>
        <Icon name={icon} size={26} color="var(--muted-slate)" />
      </div>
      <h3 style={TITLE_STYLE}>{title}</h3>
      {body && <p style={BODY_STYLE}>{body}</p>}
      {helpers && <div style={HELPERS_STYLE}>{helpers}</div>}
      {(action || secondary) && (
        <div style={ACTION_ROW_STYLE}>
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
